import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useMotionValue, useMotionValueEvent, useInView } from 'framer-motion';
import '../housestyle.css';
import './Home.css';
import api from '../API/Api';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { McIcon } from '../components/CraftingGrid';
import DiscordMarkdown from '../components/DiscordMarkdown';

const RARITY_COLORS = {
  common: 'var(--tier-common)',
  uncommon: 'var(--tier-uncommon)',
  rare: 'var(--tier-rare)',
  epic: 'var(--tier-epic)',
  legendary: 'var(--tier-legendary)',
};

const SLOT_SHORT = { lmb: 'LMB', rmb: 'RMB', f: 'F', ult: 'Q' };

/* ── Roots 3D scroll showcase ────────────────────────────────── */

function ShowcaseCard({ root, index, count, progress }) {
  // offset in "cards" from the centered position, wrapped so the carousel
  // loops: the last card shows up just left of the first and vice versa
  const offset = useTransform(progress, v => {
    let o = ((index - v) % count + count) % count;
    if (o > count / 2) o -= count;
    return o;
  });

  const x = useTransform(offset, v => `calc(${v.toFixed(4)} * min(500px, 88vw))`);
  const rotateY = useTransform(offset, [-2, 0, 2], [52, 0, -52]);
  const scale = useTransform(offset, [-2, -1, 0, 1, 2], [0.72, 0.86, 1, 0.86, 0.72]);
  const opacity = useTransform(offset, [-2.4, -1, 0, 1, 2.4], [0, 0.55, 1, 0.55, 0]);
  const zIndex = useTransform(offset, v => 100 - Math.round(Math.abs(v) * 10));

  const actives = (root.abilities || []).filter(a => a.slot !== 'passive');
  const passives = (root.abilities || []).filter(a => a.slot === 'passive');

  return (
    <motion.div
      className="home-rs-card"
      style={{
        '--rc': root.color,
        '--rc2': root.gradient_end || root.color,
        x, rotateY, scale, opacity, zIndex,
        y: '-50%',
      }}
    >
      <div className="home-rs-card-inner">
        <div className="home-rs-bigname" aria-hidden="true">{root.name}</div>

        <div className="home-rs-top">
          <div className="home-rs-orb">
            <McIcon materialKey={root.material} size={60} />
          </div>
          <div style={{ minWidth: 0 }}>
            <span className="home-rs-rarity" style={{ color: RARITY_COLORS[root.rarity] || 'var(--text-muted)' }}>
              {root.rarity}
            </span>
            <h3 className="home-rs-name">{root.name}</h3>
            <span className="home-rs-playstyle">{root.playstyle || 'Root'}</span>
          </div>
        </div>

        <p className="home-rs-desc">{root.description}</p>

        <div className="home-rs-abilities">
          {actives.map(a => (
            <span key={a.id ?? `${a.slot}-${a.name}`} className="home-rs-chip">
              <span>{SLOT_SHORT[a.slot] || a.slot}</span>{a.name}
            </span>
          ))}
          {passives.length > 0 && (
            <span className="home-rs-chip home-rs-chip--muted">
              +{passives.length} passive{passives.length > 1 ? 's' : ''}
            </span>
          )}
        </div>

        <Link to={`/wiki/root/${root.slug}`} className="home-rs-link">
          View in the Wiki →
        </Link>
      </div>
    </motion.div>
  );
}

function RootsShowcase({ roots }) {
  const visible = (roots || []).filter(r => Number(r.enabled) !== 0 && r.status !== 'stub');
  const count = visible.length;

  const sectionRef = useRef(null);
  const dragStartX = useRef(null);
  const suppressClick = useRef(false);
  const [paused, setPaused] = useState(false);
  const inView = useInView(sectionRef, { amount: 0.4 });

  // carousel position in card units, unbounded so it can loop endlessly;
  // spring-smoothed into the card transforms
  const target = useMotionValue(0);
  const progress = useSpring(target, { stiffness: 140, damping: 26, mass: 0.4 });

  const [active, setActive] = useState(0);
  useMotionValueEvent(progress, 'change', v => {
    setActive(((Math.round(v) % count) + count) % count);
  });

  // autoplay: advance one card every 5s while visible and not interacted with
  useEffect(() => {
    if (count < 2 || paused || !inView) return;
    const t = setInterval(() => {
      target.set(Math.round(target.get()) + 1);
    }, 5000);
    return () => clearInterval(t);
  }, [count, paused, inView, target]);

  if (count === 0) return null;

  const activeRoot = visible[active] || visible[0];

  // move to card i via the shortest way around the loop
  function jumpTo(i) {
    const cur = target.get();
    const curMod = ((cur % count) + count) % count;
    let delta = ((i - curMod) % count + count) % count;
    if (delta > count / 2) delta -= count;
    target.set(cur + delta);
  }

  // drag on the stage: cards follow the pointer live, snap to nearest on release
  function onPointerDown(e) {
    dragStartX.current = { x: e.clientX, start: target.get(), moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e) {
    const d = dragStartX.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 5) d.moved = true;
    if (!d.moved) return;
    // one card-width of drag = one card of progress, no clamping so it loops
    const spacing = Math.min(500, window.innerWidth * 0.88);
    target.set(d.start - dx / spacing);
  }

  function onPointerUp() {
    const d = dragStartX.current;
    dragStartX.current = null;
    if (!d || !d.moved) return;
    suppressClick.current = true;
    target.set(Math.round(target.get()));
  }

  function onClickCapture(e) {
    if (suppressClick.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressClick.current = false;
    }
  }

  return (
    <section
      ref={sectionRef}
      className="home-roots-show"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
    >
      <div className="home-rs-sticky">
        <div className="home-rs-glowwrap" aria-hidden="true">
          <div className="home-rs-glow" style={{ backgroundColor: activeRoot.color }} />
        </div>

        <div className="home-rs-header">
          <span className="sec-eyebrow">The Roots System</span>
          <h2 className="home-rs-title">
            {count} elemental powers. <em>Which one is yours?</em>
          </h2>
          <Link to="/wiki/roots" className="home-rs-browse">Browse all roots →</Link>
        </div>

        <div className="home-rs-stagewrap">
          <button
            className="home-rs-arrow home-rs-arrow--left"
            aria-label="Previous root"
            onClick={() => jumpTo(active - 1)}
          >
            ‹
          </button>
          <div
            className="home-rs-stage"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onClickCapture={onClickCapture}
          >
            {visible.map((root, i) => (
              <ShowcaseCard
                key={root.slug}
                root={root}
                index={i}
                count={count}
                progress={progress}
              />
            ))}
          </div>
          <button
            className="home-rs-arrow home-rs-arrow--right"
            aria-label="Next root"
            onClick={() => jumpTo(active + 1)}
          >
            ›
          </button>
        </div>

        <div className="home-rs-footer">
          <span className="home-rs-active-name" style={{ color: activeRoot.color }}>
            {activeRoot.name}
          </span>
          <div className="home-rs-dots">
            {visible.map((r, i) => (
              <button
                key={r.slug}
                aria-label={r.name}
                className={`home-rs-dot${i === active ? ' active' : ''}`}
                style={i === active ? { background: r.color } : undefined}
                onClick={() => jumpTo(i)}
              />
            ))}
          </div>
          <div className="home-rs-hintrow">
            <span className="home-rs-hint">Drag, or use the arrows to explore</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── IP bar ──────────────────────────────────────────────────── */

function IPBar() {
  const [toastVisible, setToastVisible] = useState(false);
  const timerRef = useRef(null);

  function copyIP() {
    navigator.clipboard.writeText('play.simplemcnetwork.net').catch(() => { });
    setToastVisible(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setToastVisible(false), 1800);
  }

  return (
    <>
      <div className="home-ip-bar">
        <div className="home-ip-item">
          <span className="home-ip-label">IP</span>
          <span className="home-ip-value">play.simplemcnetwork.net</span>
          <button className="home-ip-copy" onClick={copyIP}>Copy</button>
        </div>
        <div className="home-ip-divider" />
        <div className="home-ip-item">
          <span className="home-ip-label">Version</span>
          <span className="home-ip-value">1.21+</span>
        </div>
        <div className="home-ip-divider" />
        <div className="home-ip-item">
          <span className="home-ip-label">Status</span>
          <span className="home-ip-value home-ip-value--development">● in development</span>
        </div>
        <div className="home-ip-divider" />
        <div className="home-ip-item">
          <span className="home-ip-label">Mode</span>
          <span className="home-ip-value" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Java</span>
        </div>
        <div className="home-ip-divider" />
        <div className="home-ip-item">
          <span className="home-ip-label">Cracked?</span>
          <span className="home-ip-value" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Yes</span>
        </div>
      </div>
      <div className={`home-copied-toast${toastVisible ? ' show' : ''}`}>IP Copied!</div>
    </>
  );
}

function SectionHeader({ eyebrow, title, em }) {
  return (
    <FadeIn duration={0.6}>
      <div className="home-sec-head">
        <span className="sec-eyebrow">{eyebrow}</span>
        <h2 className="home-sec-title">
          {title}{em && <> <em>{em}</em></>}
        </h2>
      </div>
    </FadeIn>
  );
}

function FeatureCard({ material, title, desc, color, variant }) {
  const isHero = variant === 'hero';
  return (
    <div
      className={`home-feat-card${variant ? ` home-feat-card--${variant}` : ''}`}
      style={{ '--fc': color }}
    >
      <div className="home-feat-bar" />
      {isHero && (
        <div className="home-feat-bigicon" aria-hidden="true">
          <McIcon materialKey={material} size={170} />
        </div>
      )}
      <div className="home-feat-orb">
        <McIcon materialKey={material} size={isHero ? 46 : 30} />
      </div>
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}

function Step({ num, title, children }) {
  return (
    <div className="home-step">
      <div className="home-step-num">{num}</div>
      <h4>{title}</h4>
      <p>{children}</p>
    </div>
  );
}

function NewsCard({ item }) {
  const isOffer = item.type === 'offer';
  return (
    <div className={`home-news-card ${isOffer ? 'home-news-card--offer' : ''} ${item.image_url ? 'home-news-card--has-img' : ''}`}>
      <div className="home-news-content">
        <div className="home-news-header">
          <span className={`home-news-badge ${item.type}`}>
            {isOffer ? 'Limited Offer' : item.type}
          </span>
          <span className="home-news-date">
            {new Date(item.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </span>
        </div>
        <h3>{item.title}</h3>
        <DiscordMarkdown text={item.content} className="home-news-body" />
        <div className="home-news-footer">
          <span className="home-news-author">By {item.author || 'Staff'}</span>
        </div>
      </div>
      {item.image_url && (
        <div className="home-news-img">
          <img src={item.image_url} alt={item.title} />
        </div>
      )}
    </div>
  );
}

const features = [
  { material: 'BLAZE_POWDER', title: 'The Roots System', desc: 'Equip an elemental Root: Earth, Fire, Void, Dragon and many more. Each has unique abilities, passives and an ultimate. Unlock rarer Roots, tier them up, and shape how you fight.', color: '#e84c3d', variant: 'hero' },
  { material: 'EXPERIENCE_BOTTLE', title: 'Tiers & Rarities', desc: 'Roots range from Common to Legendary. Upgrade yours through three tiers to unlock its full strength.', color: '#f0ab20' },
  { material: 'CHEST_MINECART', title: 'Supply Drops', desc: 'Crates parachute in across the map on a schedule. Race to the drop zone and loot before someone else does.', color: '#52c078' },
  { material: 'MAGMA_BLOCK', title: 'Meteor Events', desc: 'Meteors tear through the sky and crash into the world, leaving craters packed with rare loot.', color: '#e8642c' },
  { material: 'WHITE_BANNER', title: 'Teams', desc: 'Group up with friends. Some Roots even heal, shield and buff teammates in combat.', color: '#5a9ee0' },
  { material: 'FIREWORK_ROCKET', title: 'Events', desc: 'Regular seasonal events and competitions with exclusive rewards and community engagement.', color: '#a070e0' },
  { material: 'GOLD_INGOT', title: 'Fun Community', desc: 'Active player base with regular polls, staff interaction, and a welcoming environment for all.', color: '#f0ab20' },
  { material: 'OMINOUS_TRIAL_KEY', title: 'Cracked Supported', desc: 'Play with or without a premium Minecraft account. Everyone welcome.', color: '#7fd4ff', variant: 'wide' },
];

function useCountdown(targetDate) {
  const calc = () => {
    if (!targetDate) return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: false };
    const diff = new Date(targetDate) - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: true };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
      ended: false,
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    if (!targetDate) return;
    const t = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(t);
  }, [targetDate]);
  return time;
}

function LaunchBanner({ season }) {
  const launchDate = season?.start_date ?? null;
  const countdown = useCountdown(launchDate);
  const isLive = season?.is_live === '1' || season?.is_live === 1;
  const seasonNum = season?.number ?? 2;

  if (!season) return null;

  return (
    <div className="home-launch-banner">
      <div className="home-launch-inner container">
        {isLive || countdown.ended ? (
          <>
            <div className="home-launch-label">Season {seasonNum} is</div>
            <div className="home-launch-live">
              <span className="home-launch-live-dot" />
              LIVE
            </div>
            <div className="home-launch-ip">play.simplemcnetwork.net</div>
          </>
        ) : !launchDate ? (
          <>
            <div className="home-launch-label">Season {seasonNum} launch date</div>
            <div className="home-launch-tba">Yet to be found</div>
            <div className="home-launch-date">Keep an eye on the Discord for announcements</div>
          </>
        ) : (
          <>
            <div className="home-launch-label">Season {seasonNum} launches in</div>
            <div className="home-launch-countdown">
              {[['Days', countdown.days], ['Hours', countdown.hours], ['Min', countdown.minutes], ['Sec', countdown.seconds]].map(([label, val]) => (
                <div key={label} className="home-launch-cd-block">
                  <span className="home-launch-cd-val">{String(val).padStart(2, '0')}</span>
                  <span className="home-launch-cd-label">{label}</span>
                </div>
              ))}
            </div>
            {launchDate && (
              <div className="home-launch-date">
                {new Date(launchDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const TRAILER_URL = "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/trailer/simplemc_trailer_season-2.mp4";

function TrailerModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="home-trailer-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
        >
          <motion.div
            className="home-trailer-modal"
            initial={{ opacity: 0, scale: 0.88, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="home-trailer-modal-head">
              <div className="home-trailer-modal-title">
                Simple<em>MC</em> <span>· Official Trailer</span>
              </div>
              <button className="home-trailer-close" onClick={onClose} aria-label="Close trailer">✕</button>
            </div>
            <div className="home-trailer-frame">
              <video src={TRAILER_URL} controls autoPlay playsInline />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Hero({ seasonNum, visits }) {
  const { scrollY } = useScroll();
  const vidY = useTransform(scrollY, [0, 700], [0, 190]);
  const logoY = useTransform(scrollY, [0, 500], [0, 110]);
  const logoOpacity = useTransform(scrollY, [0, 420], [1, 0]);
  const [trailerOpen, setTrailerOpen] = useState(false);

  return (
    <div className="home-hero">
      <motion.video
        autoPlay muted loop playsInline
        src={TRAILER_URL}
        style={{ y: vidY, scale: 1.14 }}
      />
      <div className="home-hero-overlay" />
      <div className="home-hero-logo">
        <motion.div style={{ y: logoY, opacity: logoOpacity }}>
          <FadeIn y={30} duration={0.8}>
            <div className="home-hero-logo-text">Simple<em>MC</em></div>
            <div className="home-hero-logo-sub">
              Season {seasonNum} · <span className="home-hero-roots">Roots</span> Survival SMP
            </div>
            <div>
              <button className="home-trailer-btn" onClick={() => setTrailerOpen(true)}>
                <span className="home-trailer-btn-icon">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                    <path d="M8 5.14v13.72c0 .8.87 1.3 1.56.88l10.8-6.86a1.04 1.04 0 0 0 0-1.76L9.56 4.26A1.04 1.04 0 0 0 8 5.14z" />
                  </svg>
                </span>
                Watch Trailer
              </button>
            </div>
          </FadeIn>
        </motion.div>
      </div>
      <TrailerModal open={trailerOpen} onClose={() => setTrailerOpen(false)} />
      <div className="home-hero-scrollhint" aria-hidden="true">
        <span>Scroll</span>
        <div className="home-hero-scrollhint-chevron">⌄</div>
      </div>
      <div className="home-hero-video-label">Website Visits: {visits}</div>
    </div>
  );
}

export default function HomePage() {
  const [visits, setVisits] = useState(null);
  const [news, setNews] = useState([]);
  const [roots, setRoots] = useState([]);
  const [season, setSeason] = useState(null);

  useEffect(() => {
    api('trackvisit').then(res => {
      if (res.isSuccess) setVisits(res.data?.visits ?? null);
    });
    api('getnews').then(res => {
      if (res.isSuccess) setNews(res.data || []);
    });
    api('getroots').then(res => {
      if (res.isSuccess) setRoots(res.data || []);
    });
    api('getseasoninfo').then(res => {
      if (res.isSuccess) setSeason(res.data);
    });
  }, []);

  const seasonNum = season?.number ?? 2;

  return (
    <div style={{ paddingBottom: '4rem' }}>

      <Hero seasonNum={seasonNum} visits={visits} />

      <IPBar />

      <LaunchBanner season={season} />

      <RootsShowcase roots={roots} />

      <div className="container" style={{ paddingTop: '3.5rem' }}>

        <SectionHeader eyebrow="Stay in the loop" title="Latest" em="News & Offers" />

        <FadeIn duration={0.8}>
          {news.length > 0 ? (
            <div className="home-news-grid">
              {news.slice(0, 3).map(item => <NewsCard key={item.id} item={item} />)}
            </div>
          ) : (
            <div className="home-news-empty">
              <p>No news or offers at the moment. Check back soon!</p>
            </div>
          )}
        </FadeIn>

        <SectionHeader eyebrow="Why SimpleMC" title="Not your average" em="SMP" />

        <StaggerContainer
          className="home-features-grid"
          animate="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
        >
          {features.map((f, i) => (
            <StaggerItem key={i} className={f.variant ? `home-fi--${f.variant}` : undefined}>
              <FeatureCard material={f.material} title={f.title} desc={f.desc} color={f.color} variant={f.variant} />
            </StaggerItem>
          ))}
        </StaggerContainer>

        <SectionHeader eyebrow="Get started" title="Join in" em="4 simple steps" />

        <div className="home-join-card">
          <StaggerContainer
            className="home-steps"
            animate="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
          >
            <StaggerItem><Step num="1" title="Open Minecraft">Launch Java Edition 1.21+ or higher on your PC.</Step></StaggerItem>
            <StaggerItem><Step num="2" title="Add Server">Go to Multiplayer → Add Server and give it a name.</Step></StaggerItem>
            <StaggerItem>
              <Step num="3" title="Enter IP">
                Server address:{' '}
                <span className="home-step-ip">play.simplemcnetwork.net</span>
              </Step>
            </StaggerItem>
            <StaggerItem><Step num="4" title="Join & Play">Hit connect and spawn in. Prepare for the unexpected.</Step></StaggerItem>
          </StaggerContainer>
        </div>

      </div>
    </div>
  );
}
